import * as React from 'react';
import {useOutsideClick} from '../../hooks';

type UseSelectMenuStateParams = {
  isBlocked: boolean;
  shouldForceClose: boolean;
  isMobile: boolean;
  onOutsideClick?: () => void;
  controlledIsOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
};

export function useSelectMenuState({
  isBlocked,
  shouldForceClose,
  isMobile,
  onOutsideClick,
  controlledIsOpen,
  onMenuOpenChange,
}: UseSelectMenuStateParams) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const [hasClosedControlledOpen, setHasClosedControlledOpen] = React.useState(false);
  const isOpen =
    controlledIsOpen === false
      ? false
      : controlledIsOpen === true
        ? !hasClosedControlledOpen
        : internalIsOpen;

  React.useEffect(() => {
    if (controlledIsOpen !== true) setHasClosedControlledOpen(false);
  }, [controlledIsOpen]);

  const setIsOpen = React.useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      const value = typeof next === 'function' ? next(isOpen) : next;
      if (controlledIsOpen === true) setHasClosedControlledOpen(!value);
      if (controlledIsOpen !== false) setInternalIsOpen(value);
      onMenuOpenChange?.(value);
    },
    [isOpen, controlledIsOpen, onMenuOpenChange],
  );

  const openMenu = React.useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const closeMenu = React.useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const toggleMenu = React.useCallback(() => {
    if (isBlocked) return;
    setIsOpen(prev => !prev);
  }, [isBlocked, setIsOpen]);

  useOutsideClick({
    elementRef: containerRef,
    onOutsideClick: () => {
      setIsOpen(false);
      onOutsideClick?.();
    },
    isDisabled: !isOpen || isMobile,
  });

  React.useEffect(() => {
    if (shouldForceClose) setIsOpen(false);
  }, [shouldForceClose, setIsOpen]);

  return {containerRef, isOpen, openMenu, closeMenu, toggleMenu, setIsOpen};
}
